/**
 * Meilisearch Analytics Event Tracking
 *
 * This utility handles tracking click and conversion events for Meilisearch analytics.
 * Events are sent directly to the Meilisearch Cloud project URL (/events endpoint).
 *
 * Benefits of direct frontend approach:
 * - Faster (no backend proxy)
 * - Uses the same search API key that's already exposed for queries
 * - CORS enabled by Meilisearch
 */

// Module-level variables to store Meilisearch config
let cachedMeilisearchKey = null;
let cachedMeilisearchHost = null;

/**
 * Set the Meilisearch API key and host
 * This should be called by Vue components using usePage()
 * @param {string} apiKey - The Meilisearch API key
 * @param {string} [host] - The Meilisearch host URL
 */
export function setMeilisearchKey(apiKey, host) {
    cachedMeilisearchKey = apiKey;
    if (host) {
        cachedMeilisearchHost = host;
    }
}

/**
 * Get the cached Meilisearch API key
 * @returns {string|null}
 */
function getMeilisearchKey() {
    return cachedMeilisearchKey;
}

/**
 * Get or create a unique user ID
 * Uses localStorage to persist the ID across sessions
 */
function getUserId() {
    if (typeof localStorage === 'undefined') {
        return 'ssr_anonymous';
    }

    let userId = localStorage.getItem('meilisearch_user_id');
    
    if (!userId) {
        // Generate a simple unique ID (you can use a more sophisticated method)
        userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('meilisearch_user_id', userId);
    }
    
    // If user is authenticated, prefer using their actual user ID
    try {
        const page = window.Inertia?.page || {};
        const user = page?.props?.auth?.user;
        if (user && user.id) {
            userId = 'user_' + user.id;
        }
    } catch (e) {
        // Ignore errors if Inertia is not available
    }
    
    return userId;
}

/**
 * Export getUserId for use in other modules
 * This ID is used in the X-MS-USER-ID header for Meilisearch analytics
 */
export function getMeilisearchUserId() {
    return getUserId();
}

/**
 * Send an analytics event to Meilisearch
 * Sends directly to the Meilisearch Cloud project URL /events endpoint
 *
 * @param {Object} eventData - The event data to send
 * @param {string} eventData.eventType - 'click' or 'conversion'
 * @param {string} eventData.eventName - Descriptive name for the event
 * @param {string} eventData.indexUid - The index identifier
 * @param {string} eventData.objectId - The document primary key
 * @param {string} [eventData.objectName] - Description of the document
 * @param {number} [eventData.position] - Position in search results (0-indexed)
 * @param {string} [eventData.queryUid] - The search query ID from metadata.queryUid
 */
async function sendAnalyticsEvent(eventData) {
    const userId = getUserId();
    const apiKey = getMeilisearchKey();
    const host = cachedMeilisearchHost;

    // Skip if no user ID, API key, or host (silently)
    if (!userId || !apiKey || !host) {
        if (window.location.hostname === 'localhost' || window.location.search.includes('debug')) {
            console.log('[Meilisearch Analytics] Skipping event (not yet initialized)', {
                eventType: eventData.eventType,
                hasUserId: !!userId,
                hasApiKey: !!apiKey,
                hasHost: !!host
            });
        }
        return;
    }

    // Build the event payload
    const payload = {
        eventType: eventData.eventType,
        eventName: eventData.eventName || (eventData.eventType === 'click' ? 'Search Result Clicked' : 'Conversion'),
        indexUid: eventData.indexUid,
        userId: userId,
        objectId: eventData.objectId,
    };

    // Add optional fields
    if (eventData.objectName) {
        payload.objectName = eventData.objectName;
    }

    if (eventData.position !== undefined && eventData.position !== null) {
        payload.position = eventData.position;
    }

    if (eventData.queryUid) {
        payload.queryUid = eventData.queryUid;
    }

    try {
        // Send to Meilisearch Cloud project URL /events endpoint
        const eventsUrl = `${host.replace(/\/$/, '')}/events`;
        const response = await fetch(eventsUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apiKey}`,
                'X-MS-USER-ID': userId,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            console.warn('[Meilisearch Analytics] Event failed:', response.status, response.statusText);
        } else {
            console.log('[Meilisearch Analytics] Event sent successfully:', eventData.eventType, eventData.eventName);
        }
    } catch (error) {
        // Silently fail - don't disrupt user experience
        console.warn('[Meilisearch Analytics] Failed to send event:', error);
    }
}

/**
 * Track a click event when a user clicks on a search result
 * 
 * @param {Object} params - Click event parameters
 * @param {string} params.indexUid - The index identifier
 * @param {string} params.objectId - The document primary key (usually 'id')
 * @param {string} [params.objectName] - Description of the document
 * @param {number} [params.position] - Position in search results (0-indexed)
 * @param {string} [params.queryUid] - The search query ID
 */
export function trackClick({ indexUid, objectId, objectName, position, queryUid }) {
    sendAnalyticsEvent({
        eventType: 'click',
        eventName: 'Search Result Clicked',
        indexUid,
        objectId,
        objectName,
        position,
        queryUid,
    });
}

/**
 * Track a conversion event when a user performs a meaningful action
 * 
 * @param {Object} params - Conversion event parameters
 * @param {string} params.indexUid - The index identifier
 * @param {string} params.objectId - The document primary key
 * @param {string} [params.objectName] - Description of the document
 * @param {string} [params.eventName] - Custom event name (defaults to 'Conversion')
 * @param {string} [params.queryUid] - The search query ID (from URL parameter)
 */
export function trackConversion({ indexUid, objectId, objectName, eventName, queryUid }) {
    // Check URL for queryUid parameter
    if (!queryUid) {
        const urlParams = new URLSearchParams(window.location.search);
        queryUid = urlParams.get('queryUid');
    }
    
    sendAnalyticsEvent({
        eventType: 'conversion',
        eventName: eventName || 'Conversion',
        indexUid,
        objectId,
        objectName,
        queryUid,
    });
}

/**
 * Get query UID from URL parameters
 */
export function getQueryUidFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('queryUid');
}

/**
 * Get index UID based on the current route
 * This maps routes to their corresponding Meilisearch index names
 */
export function getIndexUidFromRoute() {
    try {
        const page = window.Inertia?.page || {};
        const props = page?.props?.integrations?.meilisearch || {};
        const pathname = window.location.pathname;
        
        // Map routes to index names
        if (pathname.includes('/lesson-plans/') || pathname.includes('/external-resources/')) {
            return props.teachingMaterialsIndex || 'teaching_materials';
        } else if (pathname.includes('/teacher-guides/')) {
            return props.teacherGuidesIndex || 'teacher_guides';
        } else if (pathname.includes('/climate-change-explainers/')) {
            return props.climateExplainersIndex || 'climate_explainers';
        } else if (pathname.includes('/professional-development/')) {
            return props.pdIndex || 'opportunities';
        } else if (pathname.includes('/news/')) {
            return props.newsIndex || 'news';
        }
        
        return null;
    } catch (e) {
        return null;
    }
}

